import type { ReactNode } from 'react';
import { usdFixed } from '../../lib/format';
import { SourceMark } from '../SourceMark';

type SourceState = 'failed' | 'served' | 'unused';

export interface RoutingLadderSource {
  /** the anonymous source's animal key (e.g. "bison"), which seeds its mark */
  artworkKey: string;
  /** the source's public name, as the catalog shows it */
  name: string;
  /** what this source charges for one call, in USD */
  priceUsd: number;
  /** what happened on this call, in the reader's words */
  outcome: string;
  state: SourceState;
}

export interface RoutingLadderProps {
  /** the request line shown above the ladder */
  request: string;
  /** sources in the order the gateway tries them */
  sources: RoutingLadderSource[];
  /** what the call ends up costing, e.g. "$0.001" */
  total: string;
  caption?: ReactNode;
  ariaLabel?: string;
}

const rowTone: Record<SourceState, string> = {
  failed: 'border-line bg-surface',
  served: 'border-accent bg-accent-soft',
  unused: 'border-line bg-surface opacity-60',
};

const badgeTone: Record<SourceState, string> = {
  failed: 'border-line text-text-3',
  served: 'border-accent text-accent',
  unused: 'border-line text-text-3',
};

/**
 * The failover ladder for one endpoint: the sources an endpoint can use, in the
 * order this request tried them, each with its public identity, its own price,
 * and what it did. The source that served is accented and is the only one
 * charged. Customer-facing copy says "source", never the internal word.
 * Theme-aware (tokens only); stacks on narrow viewports.
 */
export const RoutingLadder = ({ request, sources, total, caption, ariaLabel }: RoutingLadderProps) => (
  <figure className="my-6">
    <div
      className="rounded-10 border border-line bg-surface-2 p-4"
      role="img"
      aria-label={ariaLabel ?? `${request}: ${sources.map((s) => `${s.name} ${usdFixed(s.priceUsd, 4)} ${s.outcome}`).join('; ')}. You pay ${total}.`}
    >
      <div className="rounded-8 border border-line bg-surface px-3 py-2 font-mono text-12 text-text-2">{request}</div>

      <p className="mt-3 mb-2 text-11 uppercase tracking-wide text-text-3">
        Sources that can serve this request, cheapest first
      </p>

      <ol className="m-0 flex list-none flex-col gap-2 p-0">
        {sources.map((source, i) => (
          <li key={source.name} className={`m-0 flex items-center gap-3 rounded-8 border px-3 py-2 ${rowTone[source.state]}`}>
            <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border text-11 ${badgeTone[source.state]}`}>
              {i + 1}
            </span>
            <SourceMark id={`anonymous-${source.artworkKey}`} size={32} />
            <span className="min-w-0 flex-1">
              <span className="block text-14 font-semibold text-text">{source.name}</span>
              <span className="block text-12 text-text-2">{source.outcome}</span>
            </span>
            <span className={`shrink-0 font-mono text-13 ${source.state === 'served' ? 'text-accent' : 'text-text-3'}`}>
              {usdFixed(source.priceUsd, 4)}
            </span>
          </li>
        ))}
      </ol>

      <p className="mt-3 text-13 text-text-2">
        You pay <span className="font-mono font-semibold text-text">{total}</span>, once, for the source that returned the page.
      </p>
    </div>
    {caption ? <figcaption className="mt-2 text-center text-13 text-text-3">{caption}</figcaption> : null}
  </figure>
);
